import {
  type Control,
  type FieldErrors,
  type UseFormGetValues,
  type UseFormRegister,
  type UseFormSetValue,
  useWatch,
} from "react-hook-form";

import type { SubmitAppFormValues } from "../SubmitApp.schema";
import type { SubmitAppRedirectField } from "../SubmitApp.types";
import { MAX_REDIRECT_URIS } from "../SubmitApp.utils";

interface UseSubmitAppRedirectsOptions {
  control: Control<SubmitAppFormValues>;
  getValues: UseFormGetValues<SubmitAppFormValues>;
  redirectErrors: FieldErrors<SubmitAppFormValues>["redirect_uri"];
  register: UseFormRegister<SubmitAppFormValues>;
  setValue: UseFormSetValue<SubmitAppFormValues>;
}

export const useSubmitAppRedirects = ({
  control,
  getValues,
  redirectErrors,
  register,
  setValue,
}: UseSubmitAppRedirectsOptions) => {
  const redirectUris = useWatch({ control, name: "redirect_uri" });
  const redirectCount = redirectUris.length;
  const redirects: SubmitAppRedirectField[] = Array.from(
    { length: redirectCount },
    (_, index) => ({
      id: `redirect-${index}`,
      input: register(`redirect_uri.${index}`),
      error: redirectErrors?.[index]?.message,
    }),
  );

  const onAddRedirect = () => {
    if (redirectCount >= MAX_REDIRECT_URIS) {
      return;
    }
    setValue(`redirect_uri.${redirectCount}`, "", {
      shouldDirty: true,
    });
  };

  const onRemoveRedirect = (index: number) => {
    const nextRedirectUris = getValues("redirect_uri").filter(
      (_redirectUri, redirectIndex) => redirectIndex !== index,
    );
    setValue("redirect_uri", nextRedirectUris, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const canAddRedirect = redirectCount < MAX_REDIRECT_URIS;

  return {
    redirects,
    canAddRedirect,
    onAddRedirect,
    onRemoveRedirect,
  };
};
